import { GetProposalQuery } from '@/gql/graphql';

type Proposal = GetProposalQuery['proposal'];

/**
 * The actions the send panel's dropdown offers. Narrowed from the availability
 * fields so renaming one on the backend fails to compile here.
 */
export type ProposalLifecycleActionKey = Extract<
  keyof Proposal['actionAvailability'],
  | 'markAsSent'
  | 'markAsAccepted'
  | 'markAsDeclined'
  | 'removeAccepted'
  | 'replace'
>;

/** What the panel needs of a Proposal to decide its actions. */
export type ProposalLifecycleFields = Pick<
  Proposal,
  'sentAt' | 'acceptedAt' | 'declinedAt' | 'actionAvailability'
>;

export type ProposalLifecycleAction = {
  key: ProposalLifecycleActionKey;
  label: string;
  disabled: boolean;
  disabledReasons: string[];
};

type ProposalLifecycleContext = {
  isSent: boolean;
  isAccepted: boolean;
  isDeclined: boolean;
};

type ActionDescriptor = {
  key: ProposalLifecycleActionKey;
  label: string;
  isRendered: (context: ProposalLifecycleContext) => boolean;
};

/**
 * This defines the order of actions, and if they should be displayed at all.
 * "Mark as sent", does not need to be display to the user after the proposal
 * has been sent. The backend proposal.actionAvailability[] says its's not available
 * but doesn't say not to render it, that is a frontend concern.
 */
const ACTIONS: ActionDescriptor[] = [
  {
    key: 'markAsSent',
    label: 'Mark as sent',
    isRendered: ({ isSent }) => !isSent,
  },
  {
    key: 'markAsAccepted',
    label: 'Mark as accepted',
    isRendered: ({ isSent, isAccepted, isDeclined }) =>
      isSent && !isAccepted && !isDeclined,
  },
  {
    key: 'markAsDeclined',
    label: 'Mark as declined',
    isRendered: ({ isSent, isAccepted, isDeclined }) =>
      isSent && !isAccepted && !isDeclined,
  },
  {
    key: 'removeAccepted',
    label: 'Remove accepted',
    isRendered: ({ isSent, isAccepted }) => isSent && isAccepted,
  },
  { key: 'replace', label: 'Edit', isRendered: ({ isSent }) => isSent },
];

export const buildProposalLifecycleActions = (input: {
  proposal: ProposalLifecycleFields | undefined;
}): ProposalLifecycleAction[] => {
  const { proposal } = input;

  const context: ProposalLifecycleContext = {
    isSent: !!proposal?.sentAt,
    isAccepted: !!proposal?.acceptedAt,
    isDeclined: !!proposal?.declinedAt,
  };

  return ACTIONS.filter((action) => action.isRendered(context)).map(
    ({ key, label }) => {
      const availability = proposal?.actionAvailability[key];
      // Availability is unknown until the Proposal query resolves. Leave the
      // action enabled rather than showing a reasonless disabled item; the
      // mutation re-checks the same gate server-side.
      const disabled = availability ? !availability.result : false;

      return {
        key,
        label,
        disabled,
        disabledReasons: disabled ? availability!.reasons : [],
      };
    }
  );
};
